import { Amount } from '@/models/Price';

export const discountTypes = ['fixedAmount', 'percentage', 'bxgy'] as const;
export type DiscountType = (typeof discountTypes)[number];

export const DiscountTypeLabels: Record<DiscountType, string> = {
  fixedAmount: 'Fixed Amount',
  percentage: 'Percentage',
  bxgy: 'Buy X Get Y',
};

export const parseDiscountType = (value: string): DiscountType | undefined => {
  return discountTypes.includes(value as DiscountType) ? (value as DiscountType) : undefined;
};

/**
 * Returns the DiscountType of the first discount.
 */
export const getDiscountType = (discount: Discount | Discount[]): DiscountType | undefined => {
  if (!discount) return undefined;
  const firstDiscount = Array.isArray(discount) ? discount[0] : discount;
  return firstDiscount?.discountType;
};

export const isFixedAmountDiscount = (discount: Discount): discount is DiscountFixedAmount =>
  discount.discountType === 'fixedAmount';

export const isPercentageDiscount = (discount: Discount): discount is DiscountPercentage =>
  discount.discountType === 'percentage';

export const isBxgyDiscount = (discount: Discount): discount is DiscountBxgy =>
  discount.discountType === 'bxgy';

interface BaseDiscount {
  discountType: DiscountType;
}

export interface DiscountFixedAmount extends BaseDiscount {
  discountType: 'fixedAmount';
  amount: Amount;
  quantityFrom?: number;
  quantityTo?: number;
}

export interface DiscountPercentage extends BaseDiscount {
  discountType: 'percentage';
  percentage: number;
  quantityFrom?: number;
  quantityTo?: number;
}

export interface DiscountBxgy extends BaseDiscount {
  discountType: 'bxgy';
  buyQuantity: number;
  getQuantity: number;
}

export type Discount = DiscountFixedAmount | DiscountPercentage | DiscountBxgy;
